/**
 * Hyperliquid Adapter (Composite)
 *
 * The main entry point for the Hyperliquid provider. Composes all
 * sub-adapters (market data, account, trading, builder, asset meta)
 * into a single PerpsAdapter instance.
 *
 * Usage:
 *   const adapter = new HyperliquidAdapter({ testnet: true });
 *   await adapter.initialize();
 *   const book = await adapter.marketData.fetchOrderBook("BTC");
 */

import type { WalletClient } from "viem";
import type { ExchangeProvider } from "../types/provider";
import type { PerpsBuilderConfig } from "../types/trading";
import type {
  PerpsAdapter,
  PerpsMarketDataAdapter,
  PerpsAccountAdapter,
  PerpsTradingAdapter,
  PerpsTransfersAdapter,
  PerpsBuilderAdapter,
  PerpsAssetMetaAdapter,
} from "./types";
import { HyperliquidInfoClient } from "../client";
import type { HLBuilderConfig } from "../client";
import { HyperliquidMarketData } from "./market-data";
import { HyperliquidAccount } from "./account";
import { HyperliquidTrading } from "./trading";
import { HyperliquidBuilder } from "./builder";
import { HyperliquidTransfers } from "./transfers";
import { HyperliquidAssetMeta } from "./asset-meta";

// ============================================================================
// Config
// ============================================================================

export interface HyperliquidAdapterConfig {
  testnet?: boolean;
  walletClient?: WalletClient | null;
  builder?: PerpsBuilderConfig;
  /** HIP-3 dex names to include alongside main perps (e.g. ["xyz", "cash"]) */
  dexNames?: string[];
  /** Auth mode: "standard" = sessionStorage, "vsecure" = encrypted IndexedDB */
  authMode?: "standard" | "vsecure";
}

// ============================================================================
// Adapter Class
// ============================================================================

export class HyperliquidAdapter implements PerpsAdapter {
  readonly id: ExchangeProvider = "hyperliquid";
  readonly name = "Hyperliquid";

  readonly marketData: PerpsMarketDataAdapter;
  readonly account: PerpsAccountAdapter;
  readonly trading: PerpsTradingAdapter;
  readonly transfers: PerpsTransfersAdapter;
  readonly builder: PerpsBuilderAdapter;
  readonly assetMeta: PerpsAssetMetaAdapter;

  constructor(config: HyperliquidAdapterConfig = {}) {
    const testnet = config.testnet ?? false;

    // Map PerpsBuilderConfig to HLBuilderConfig (same shape)
    const hlBuilder: HLBuilderConfig | null = config.builder
      ? { address: config.builder.address, feeRate: config.builder.feeRate }
      : null;

    // Single shared REST client — inflight request deduplication prevents
    // duplicate concurrent fetches across all sub-adapters on mount.
    const sharedClient = new HyperliquidInfoClient({
      testnet,
      dexNames: config.dexNames,
    });

    this.marketData = new HyperliquidMarketData(
      testnet,
      config.dexNames,
      sharedClient,
    );
    this.account = new HyperliquidAccount(
      testnet,
      config.dexNames,
      sharedClient,
    );
    this.trading = new HyperliquidTrading({
      walletClient: config.walletClient ?? null,
      builder: config.builder,
      testnet,
      dexNames: config.dexNames,
      authMode: config.authMode,
    });
    this.transfers = new HyperliquidTransfers(testnet);
    this.builder = new HyperliquidBuilder(hlBuilder, testnet);
    this.assetMeta = new HyperliquidAssetMeta(
      testnet,
      config.dexNames,
      sharedClient,
    );
  }

  // --------------------------------------------------------------------------
  // Symbol Conversion
  // --------------------------------------------------------------------------

  toProviderSymbol(canonicalSymbol: string): string {
    // "xyz:TSLAUSDC" -> "xyz:TSLA" (HIP-3 dex, prefix embedded in canonical)
    // "BTCUSDC" -> "BTC" (main dex)
    const colonIdx = canonicalSymbol.indexOf(":");
    if (colonIdx >= 0) {
      const dex = canonicalSymbol.slice(0, colonIdx);
      const bare = canonicalSymbol.slice(colonIdx + 1).replace(/USD[CT]$/, "");
      return `${dex}:${bare}`;
    }
    return canonicalSymbol.replace(/USD[CT]$/, "");
  }

  fromProviderSymbol(providerSymbol: string): string {
    // "xyz:TSLA" -> "xyz:TSLAUSDC" (HIP-3 dex, preserve prefix)
    // "BTC" -> "BTCUSDC" (main dex)
    const colonIdx = providerSymbol.indexOf(":");
    if (colonIdx >= 0) {
      const dex = providerSymbol.slice(0, colonIdx);
      const bare = providerSymbol.slice(colonIdx + 1);
      return `${dex}:${bare}USDC`;
    }
    return `${providerSymbol}USDC`;
  }

  // --------------------------------------------------------------------------
  // Lifecycle
  // --------------------------------------------------------------------------

  async initialize(): Promise<void> {
    await this.assetMeta.initialize();
  }

  destroy(): void {
    this.trading.destroy();
  }
}

// ============================================================================
// Adapter Interfaces (PerpsAdapter and sub-adapters)
// ============================================================================

export type {
  PerpsAdapter,
  PerpsMarketDataAdapter,
  PerpsAccountAdapter,
  PerpsTradingAdapter,
  PerpsTransfersAdapter,
  PerpsBuilderAdapter,
  PerpsAssetMetaAdapter,
  PerpsTradingAdapterConfig,
  PerpsCancelResult,
  PerpsLeverageResult,
  PerpsLeverageParams,
  PerpsTriggerOrderParams,
} from "./types";

// ============================================================================
// Sub-adapter classes (advanced usage — compose your own adapter)
// ============================================================================

export { HyperliquidMarketData } from "./market-data";
export { HyperliquidAccount } from "./account";
export { HyperliquidTrading } from "./trading";
export { HyperliquidBuilder } from "./builder";
export { HyperliquidTransfers } from "./transfers";
export { HyperliquidAssetMeta } from "./asset-meta";
